import { useState, type FormEvent } from "react";
import type { DemoComment } from "@/mocks/comments";
import { comments as initialComments } from "@/mocks/comments";
import { formatCount } from "@/utils/format";

export default function CommentSection({ count }: { count: number }) {
  const [list, setList] = useState<DemoComment[]>(initialComments);
  const [value, setValue] = useState("");
  const [notice, setNotice] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const content = value.trim();
    if (!content) return;
    const newComment: DemoComment = {
      id: `local-${Date.now()}`,
      user: "我",
      avatar:
        "https://readdy.ai/api/search-image?query=Cute%20minimalist%20flat%20avatar%20illustration%20of%20a%20simple%20friendly%20user%20profile%20face%2C%20warm%20orange%20and%20cream%20palette%2C%20clean%20circular%20background%2C%20neutral%20likeable%20vibe%2C%20high%20detail%20illustration&width=200&height=200&seq=v91-avatar-me&orientation=squarish",
      content,
      time: "刚刚",
      likes: 0,
    };
    setList((current) => [newComment, ...current]);
    setValue("");
    setNotice("评论已展示在页面上（演示交互，未提交到服务器）");
    window.setTimeout(() => setNotice(""), 3000);
  };

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
      <h2 className="flex items-center gap-2 text-base font-bold text-foreground-900">
        <i className="ri-chat-3-line text-lg text-primary-500"></i>
        评论 <span className="text-sm font-normal text-foreground-400">{formatCount(count)}</span>
      </h2>

      <form onSubmit={handleSubmit} className="mt-4">
        <textarea
          value={value}
          onChange={(event) => setValue(event.target.value.slice(0, 500))}
          maxLength={500}
          rows={3}
          placeholder="说点什么吧，友善评论哦～"
          className="w-full resize-none rounded-lg border border-background-300/70 bg-background-100 p-3 text-sm text-foreground-800 outline-none transition focus:border-primary-400 focus:bg-background-50 focus:ring-2 focus:ring-primary-100"
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-xs text-foreground-400">{value.length}/500 · 演示评论，不会真实发送</span>
          <button
            type="submit"
            disabled={!value.trim()}
            className={`cursor-pointer whitespace-nowrap rounded-md px-5 py-2 text-sm font-medium transition ${
              value.trim()
                ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                : "cursor-not-allowed bg-background-200 text-foreground-400"
            }`}
          >
            发表评论
          </button>
        </div>
        {notice && (
          <p className="mt-2 flex items-center gap-1.5 rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-700">
            <i className="ri-checkbox-circle-line"></i>
            {notice}
          </p>
        )}
      </form>

      <ul className="mt-6 space-y-5">
        {list.map((comment) => (
          <li key={comment.id} className="flex gap-3">
            <img
              src={comment.avatar}
              alt={`${comment.user} 头像`}
              title={`${comment.user}`}
              className="h-9 w-9 shrink-0 rounded-full object-cover"
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="text-sm font-medium text-foreground-800">{comment.user}</span>
                <span className="text-xs text-foreground-400">{comment.time}</span>
              </div>
              <p className="mt-1 text-sm leading-relaxed text-foreground-700">{comment.content}</p>
              <div className="mt-1.5 flex items-center gap-4 text-xs text-foreground-400">
                <button
                  type="button"
                  className="flex cursor-pointer items-center gap-1 transition hover:text-accent-600"
                >
                  <i className="ri-thumb-up-line"></i>
                  {comment.likes > 0 ? formatCount(comment.likes) : "赞"}
                </button>
                <button
                  type="button"
                  className="flex cursor-pointer items-center gap-1 transition hover:text-primary-600"
                >
                  <i className="ri-chat-3-line"></i>
                  回复
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}